import axios from 'axios';
import API_BASE_URL from '../config';
import { getToken, getRefreshToken, saveToken } from './tokenUtils';

// Create an Axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
});

// Request interceptor to attach access token
api.interceptors.request.use(
  (config) => {
    const token = getToken();
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor to handle 401 Unauthorized
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If the error status is 401 and there is no originalRequest._retry flag,
    // it means the token has expired and we need to refresh it
    if (error.response && error.response.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      const accessToken = getToken();
      const refreshToken = getRefreshToken();

      if (accessToken && refreshToken) {
        try {
          const response = await axios.post(`${API_BASE_URL}/api/auth/refresh`, {
            accessToken,
            refreshToken
          });

          if (response.status === 200) {
            const { token, refreshToken: newRefreshToken } = response.data;
            saveToken(token, newRefreshToken, 7);

            // NEW: same flag, for mid-session token refreshes
            if (response.data.wasAutoLoggedOut === true) {
              localStorage.setItem('fitscan_auto_logout_pending', 'true');
            }

            // Retry the original request with the new token
            originalRequest.headers['Authorization'] = `Bearer ${token}`;
            return api(originalRequest);
          }
        } catch (refreshError) {
          // Only clear session if it was a definitive auth rejection (401/403)
          // NOT for network errors, timeouts, or cold-start failures
          const isAuthRejection = refreshError.response?.status === 401
            || refreshError.response?.status === 403;

          if (isAuthRejection) {
            localStorage.removeItem('accessToken');
            localStorage.removeItem('fitscan_user');
            // Only navigate if the app is visible — don't disrupt background state
            if (!document.hidden) {
              window.location.href = '/auth';
            }
          }
          // For network errors (no response): do nothing — 
          // the token is still in localStorage, the next foreground 
          // interaction will retry normally
          return Promise.reject(refreshError);
        }
      }
    }
    return Promise.reject(error);
  }
);

export default api;
